"use client";

import * as React from "react";
import "@/cms/blocks/registry/definitions";
import { AnimatePresence, motion } from "framer-motion";
import { AuditPanel } from "@/cms/editor/AuditPanel";
import OverlayWrapper from "@/components/ui/OverLayWrapper";
import { ConfirmOverlay } from "@/components/ui/ConfirmOverlay";
import { CustomModal } from "@/components/ui/CustomModal";
// import { useInViewRef } from "@/hooks/useInView";
import { TikSVG } from "@/components/ui/SVG";
import DotPulseLdr from "@/components/ui/DotPulseLdr";
import type { BlockBase } from "@/cms/blocks/registry/types";
import type { TagOption } from "@/cms/editor/TagPickerPanel";
import { useContentEditor } from "./useContentEditor";
import { BlockEditor } from "./block-editor/BlockEditor";
import { DetailsPanelMobile } from "./components/DetailsPanelMobile";
import { TagManagerList } from "./components/TagManagerList";
import { DiscardDraftModal } from "./components/DiscardDraftModal";
import { useSession } from "next-auth/react";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import type { AuthorSummary } from "@/cms/lib/authors";
import {
  getAuthorProfileMissingFields,
  isAuthorProfileComplete,
} from "@/cms/lib/authors";
import DateObject from "react-date-object";
import gregorian from "react-date-object/calendars/gregorian";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { ChevronDown } from "lucide-react";

const pad2 = (value: number) => value.toString().padStart(2, "0");

type ContentEditorOverlayProps = {
  contentId: string | null;
  onClose: () => void;
  fallbackPresetType?: string | null;
  onUpdated?: (payload: { id: string; data: any }) => void;
  defaultLocale?: "en" | "fa";
};

type AuthorProfileDraft = {
  userId: string;
  locale: "en" | "fa";
  email: string;
  name_en: string;
  sur_name_en: string;
  name_fa: string;
  sur_name_fa: string;
};

export function ContentEditorOverlay({
  contentId,
  onClose,
  fallbackPresetType,
  onUpdated,
  defaultLocale = "en",
}: ContentEditorOverlayProps) {
  const [locale, setLocale] = React.useState<"en" | "fa">(defaultLocale);
  const [saveSuccess, setSaveSuccess] = React.useState(false);
  const { data: session } = useSession();
  const [nameGateOpen, setNameGateOpen] = React.useState(false);
  const [nameSaving, setNameSaving] = React.useState(false);
  const [nameError, setNameError] = React.useState<string | null>(null);
  const [localUserNames, setLocalUserNames] = React.useState({
    name_en: (session?.user as any)?.name_en ?? "",
    sur_name_en: (session?.user as any)?.sur_name_en ?? "",
    name_fa: (session?.user as any)?.name_fa ?? "",
    sur_name_fa: (session?.user as any)?.sur_name_fa ?? "",
  });
  const [nameDraft, setNameDraft] = React.useState({
    name_en: "",
    sur_name_en: "",
    name_fa: "",
    sur_name_fa: "",
  });
  const [authorQueries, setAuthorQueries] = React.useState<{
    en: string;
    fa: string;
  }>({ en: "", fa: "" });
  const [activeAuthorSearchLocale, setActiveAuthorSearchLocale] = React.useState<
    "en" | "fa"
  >("fa");
  const [authorSearchResults, setAuthorSearchResults] = React.useState<
    AuthorSummary[]
  >([]);
  const [authorSearchLoading, setAuthorSearchLoading] = React.useState(false);
  const [authorProfileModalOpen, setAuthorProfileModalOpen] =
    React.useState(false);
  const [authorProfileSaving, setAuthorProfileSaving] = React.useState(false);
  const [authorProfileError, setAuthorProfileError] = React.useState<
    string | null
  >(null);
  const [authorProfileDraft, setAuthorProfileDraft] =
    React.useState<AuthorProfileDraft | null>(null);
  const debouncedAuthorQuery = useDebouncedValue(
    authorQueries[activeAuthorSearchLocale],
    250,
  );
  const [confirmState, setConfirmState] = React.useState<{
    key: "close";
    title: string;
    message: React.ReactNode;
    confirmLabel?: string;
    variant?: "danger" | "default";
    onConfirm: () => void;
  } | null>(null);
  const titleRef = React.useRef<HTMLInputElement>(null);
  // const titleInView = useInViewRef(titleRef, { root: null, threshold: 0 });
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [actionsMenuOpen, setActionsMenuOpen] = React.useState(false);
  const [discardDraftConfirmOpen, setDiscardDraftConfirmOpen] =
    React.useState(false);

  const [tagManagerOpen, setTagManagerOpen] = React.useState(false);
  const [allTags, setAllTags] = React.useState<TagOption[]>([]);
  const [tagsLoading, setTagsLoading] = React.useState(false);
  const [tagsError, setTagsError] = React.useState<string | null>(null);
  const [tagFilter, setTagFilter] = React.useState("");

  const datePickerRef = React.useRef<any>(null);
  const [durationUnit, setDurationUnit] = React.useState<"min" | "sec">("min");
  const [durationValue, setDurationValue] = React.useState<string>("");

  const [collapsedByLocale, setCollapsedByLocale] = React.useState<{
    en: Set<string>;
    fa: Set<string>;
  }>({ en: new Set(), fa: new Set() });

  const { data, state, handlers } = useContentEditor({
    contentId,
    fallbackPresetType,
    onUpdated,
  });
  const handleGoToBlock = React.useCallback(
    (blockId?: string) => {
      if (!blockId) return;
      handlers.clearError();
      window.setTimeout(() => {
        if (typeof CSS === "undefined" || typeof CSS.escape !== "function") {
          return;
        }
        const node = document.querySelector(
          `[data-block-id="${CSS.escape(blockId)}"]`,
        );
        if (node) {
          node.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 0);
    },
    [handlers],
  );

  const statusUpdating = state.isUpdatingStatus;
  const isPublished = data.isPublished;
  const isArchived = data.isArchived;
  const traceNumber = data.content?.traceNumber ?? null;
  const blocks_en = (data.blocksEn || []) as BlockBase[];
  const blocks_fa = (data.blocksFa || []) as BlockBase[];
  const selectedTags = (data.tags as TagOption[] | undefined) ?? [];
  const allowedBlockTypes = data.allowedBlockTypes;
  const hasContentId = Boolean(data.contentId);
  const authorFa = data.authorFa;
  const authorEn = data.authorEn;
  const creatorEmail = data.content?.createdBy ?? null;
  const sessionEmail = session?.user?.email ?? null;
  const isCreator =
    Boolean(creatorEmail && sessionEmail && creatorEmail === sessionEmail);
  const sessionNameFields = {
    name_en: (session?.user as any)?.name_en ?? "",
    sur_name_en: (session?.user as any)?.sur_name_en ?? "",
    name_fa: (session?.user as any)?.name_fa ?? "",
    sur_name_fa: (session?.user as any)?.sur_name_fa ?? "",
  };
  React.useEffect(() => {
    setLocalUserNames((prev) => ({
      name_en: prev.name_en || sessionNameFields.name_en || "",
      sur_name_en: prev.sur_name_en || sessionNameFields.sur_name_en || "",
      name_fa: prev.name_fa || sessionNameFields.name_fa || "",
      sur_name_fa: prev.sur_name_fa || sessionNameFields.sur_name_fa || "",
    }));
  }, [
    sessionNameFields.name_en,
    sessionNameFields.sur_name_en,
    sessionNameFields.name_fa,
    sessionNameFields.sur_name_fa,
  ]);
  const missingNameFields = {
    name_en: !localUserNames.name_en?.trim(),
    sur_name_en: !localUserNames.sur_name_en?.trim(),
    name_fa: !localUserNames.name_fa?.trim(),
    sur_name_fa: !localUserNames.sur_name_fa?.trim(),
  };
  const hasMissingNames =
    missingNameFields.name_en ||
    missingNameFields.sur_name_en ||
    missingNameFields.name_fa ||
    missingNameFields.sur_name_fa;
  const shouldGateNames =
    (!hasContentId && hasMissingNames) ||
    (hasContentId && isCreator && hasMissingNames);

  React.useEffect(() => {
    setLocale(defaultLocale);
  }, [defaultLocale, contentId]);

  React.useEffect(() => {
    if (!actionsMenuOpen) return;
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const within = target.closest('[data-actions-menu-root="true"]');
      if (within) return;
      setActionsMenuOpen(false);
    };
    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [actionsMenuOpen]);

  React.useEffect(() => {
    if (!nameGateOpen) return;
    setNameDraft({
      name_en: localUserNames.name_en || "",
      sur_name_en: localUserNames.sur_name_en || "",
      name_fa: localUserNames.name_fa || "",
      sur_name_fa: localUserNames.sur_name_fa || "",
    });
    setNameError(null);
  }, [nameGateOpen]);

  React.useEffect(() => {
    const raw = data.readingMinutes;
    if (typeof raw === "string") {
      const match = raw.trim().match(/^(\d+)\s*(min|sec)?$/i);
      if (match) {
        setDurationValue(match[1]);
        setDurationUnit(
          (match[2]?.toLowerCase() as "min" | "sec" | undefined) ?? "min",
        );
        return;
      }
      setDurationValue(raw.trim());
      setDurationUnit("min");
      return;
    }
    if (typeof raw === "number" && Number.isFinite(raw)) {
      setDurationUnit("min");
      setDurationValue(String(Math.max(0, Math.round(raw))));
    } else {
      setDurationUnit("min");
      setDurationValue("");
    }
  }, [data.readingMinutes]);

  React.useEffect(() => {
    if (!tagManagerOpen) return;
    let cancelled = false;
    const load = async () => {
      setTagsLoading(true);
      setTagsError(null);
      try {
        const res = await fetch("/api/cms/tags?take=1000");
        const json = await res.json();
        if (!res.ok) {
          throw new Error(
            json?.message || json?.error || "Failed to load tags",
          );
        }
        if (!cancelled) {
          setAllTags(Array.isArray(json?.items) ? json.items : []);
        }
      } catch (err: any) {
        if (!cancelled) setTagsError(err?.message || "Failed to load tags");
      } finally {
        if (!cancelled) setTagsLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [tagManagerOpen]);

  React.useEffect(() => {
    const enIds = new Set(blocks_en.map((b) => b.id));
    const faIds = new Set(blocks_fa.map((b) => b.id));
    setCollapsedByLocale((prev) => {
      const nextEn = new Set<string>();
      const nextFa = new Set<string>();
      prev.en.forEach((id) => {
        if (enIds.has(id)) nextEn.add(id);
      });
      prev.fa.forEach((id) => {
        if (faIds.has(id)) nextFa.add(id);
      });
      const sameSet = (a: Set<string>, b: Set<string>) => {
        if (a.size !== b.size) return false;
        for (const item of a) {
          if (!b.has(item)) return false;
        }
        return true;
      };
      if (sameSet(prev.en, nextEn) && sameSet(prev.fa, nextFa)) {
        return prev;
      }
      return { en: nextEn, fa: nextFa };
    });
  }, [blocks_en, blocks_fa]);

  React.useEffect(() => {
    const query = debouncedAuthorQuery.trim();
    if (!query) {
      setAuthorSearchResults([]);
      setAuthorSearchLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setAuthorSearchLoading(true);
      try {
        const res = await fetch(
          `/api/cms/authors/search?q=${encodeURIComponent(query)}`,
          { cache: "no-store" },
        );
        const json = await res.json();
        if (!res.ok) {
          throw new Error(
            json?.message || json?.error || "Failed to search authors.",
          );
        }
        if (!cancelled) {
          setAuthorSearchResults(Array.isArray(json?.items) ? json.items : []);
        }
      } catch (error) {
        if (!cancelled) {
          setAuthorSearchResults([]);
        }
        console.error("Failed to search authors", error);
      } finally {
        if (!cancelled) {
          setAuthorSearchLoading(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [debouncedAuthorQuery]);

  const pickerValue = React.useMemo(() => {
    if (!data.displayDate) return null;
    const date = new Date(data.displayDate);
    if (Number.isNaN(date.getTime())) return null;
    return new DateObject({
      date,
      calendar: gregorian,
      locale: persian_fa,
    });
  }, [data.displayDate]);

  const formattedDisplayDate = React.useMemo(() => {
    if (!pickerValue) return null;

    const date = pickerValue.toDate(); // same instant
    const en = new Intl.DateTimeFormat("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    }).format(date);

    const p = pickerValue.convert(persian);
    const fa = `${p.format("D")} ${p.format("MMMM")} ${p.format("YYYY")}`;

    return { en, fa };
  }, [pickerValue]);

  const datePickerValue = React.useMemo(
    () => (pickerValue ? pickerValue.convert(persian) : null),
    [pickerValue],
  );

  const handleDurationInputChange = React.useCallback(
    (value: string) => {
      setDurationValue(value);
      const trimmed = value.trim();
      const formatted = trimmed ? `${trimmed}${durationUnit}` : null;
      handlers.updateReadingMinutes(formatted);
    },
    [durationUnit, handlers],
  );

  const handleDurationUnitChange = React.useCallback(
    (unit: "min" | "sec") => {
      if (unit === durationUnit) return;
      setDurationUnit(unit);
      const trimmed = durationValue.trim();
      handlers.updateReadingMinutes(trimmed ? `${trimmed}${unit}` : null);
    },
    [durationUnit, durationValue, handlers],
  );

  const handleDatePickerChange = React.useCallback(
    (value: DateObject | DateObject[] | null) => {
      const dateObj = Array.isArray(value) ? value?.[0] : value;
      if (!dateObj) {
        handlers.updateDisplayDate(null);
        return;
      }
      const greg = dateObj.convert(gregorian);
      const native = greg.toDate();
      if (!(native instanceof Date) || Number.isNaN(native.getTime())) {
        handlers.updateDisplayDate(null);
        return;
      }
      const iso = `${native.getUTCFullYear()}-${pad2(native.getUTCMonth() + 1)}-${pad2(native.getUTCDate())}`;
      handlers.updateDisplayDate(`${iso}T00:00:00.000Z`);
    },
    [handlers],
  );

  const openDatePicker = React.useCallback(() => {
    if (isArchived) return;
    datePickerRef.current?.openCalendar?.();
  }, [isArchived]);

  const toggleCollapseBlock = React.useCallback(
    (blockId: string) => {
      setCollapsedByLocale((prev) => {
        const next = new Set(prev[locale]);
        if (next.has(blockId)) next.delete(blockId);
        else next.add(blockId);
        return { ...prev, [locale]: next };
      });
    },
    [locale],
  );

  const toggleCollapseAll = React.useCallback(() => {
    setCollapsedByLocale((prev) => {
      const ids = (locale === "en" ? blocks_en : blocks_fa).map((b) => b.id);
      const current = prev[locale];
      const allCollapsed = ids.length > 0 && ids.every((id) => current.has(id));
      const nextSet = allCollapsed ? new Set<string>() : new Set<string>(ids);
      return { ...prev, [locale]: nextSet };
    });
  }, [blocks_en, blocks_fa, locale]);

  const handleAuthorQueryChange = React.useCallback(
    (nextLocale: "en" | "fa", value: string) => {
      setActiveAuthorSearchLocale(nextLocale);
      setAuthorQueries((prev) => ({ ...prev, [nextLocale]: value }));
      if (!value.trim()) {
        setAuthorSearchResults([]);
      }
    },
    [],
  );

  const openAuthorProfileModal = React.useCallback(
    (nextLocale: "en" | "fa", author: AuthorSummary) => {
      setAuthorProfileDraft({
        userId: author.id,
        locale: nextLocale,
        email: author.email,
        name_en: author.name_en ?? "",
        sur_name_en: author.sur_name_en ?? "",
        name_fa: author.name_fa ?? "",
        sur_name_fa: author.sur_name_fa ?? "",
      });
      setAuthorProfileError(null);
      setAuthorProfileModalOpen(true);
    },
    [],
  );

  const handleAuthorSelect = React.useCallback(
    (nextLocale: "en" | "fa", author: AuthorSummary) => {
      handlers.setAuthor(nextLocale, author);
      setAuthorQueries((prev) => ({ ...prev, [nextLocale]: "" }));
      setAuthorSearchResults([]);
      if (!isAuthorProfileComplete(author)) {
        openAuthorProfileModal(nextLocale, author);
      }
    },
    [handlers, openAuthorProfileModal],
  );

  const handleAuthorClear = React.useCallback(
    (nextLocale: "en" | "fa") => {
      handlers.setAuthor(nextLocale, null);
      setAuthorQueries((prev) => ({ ...prev, [nextLocale]: "" }));
      setAuthorSearchResults([]);
    },
    [handlers],
  );

  const handleSaveAuthorProfile = React.useCallback(async () => {
    if (!authorProfileDraft) return;
    const payload = {
      name_en: authorProfileDraft.name_en.trim(),
      sur_name_en: authorProfileDraft.sur_name_en.trim(),
      name_fa: authorProfileDraft.name_fa.trim(),
      sur_name_fa: authorProfileDraft.sur_name_fa.trim(),
    };
    const missing = getAuthorProfileMissingFields(payload);
    if (Object.values(missing).some(Boolean)) {
      setAuthorProfileError("Please complete all required author profile fields.");
      return;
    }
    setAuthorProfileSaving(true);
    setAuthorProfileError(null);
    try {
      const res = await fetch(
        `/api/cms/authors/${authorProfileDraft.userId}/profile`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      const json = await res.json();
      if (!res.ok) {
        throw new Error(
          json?.message || json?.error || "Failed to update author profile.",
        );
      }
      const updatedAuthor = json?.user as AuthorSummary;
      if (authorFa?.id === updatedAuthor.id) {
        handlers.setAuthor("fa", updatedAuthor);
      }
      if (authorEn?.id === updatedAuthor.id) {
        handlers.setAuthor("en", updatedAuthor);
      }
      setAuthorProfileModalOpen(false);
      setAuthorProfileDraft(null);
    } catch (error: any) {
      setAuthorProfileError(
        error?.message || "Failed to update author profile.",
      );
    } finally {
      setAuthorProfileSaving(false);
    }
  }, [authorEn?.id, authorFa?.id, authorProfileDraft, handlers]);

  const requestClose = React.useCallback(() => {
    if (state.isSaving || statusUpdating) {
      return;
    }
    if (state.isDirty) {
      setConfirmState({
        key: "close",
        title: "Discard changes?",
        message:
          "You have unsaved changes. If you close now, those changes will be lost.",
        confirmLabel: "Discard",
        variant: "danger",
        onConfirm: () => {
          setConfirmState(null);
          onClose();
        },
      });
      return;
    }
    onClose();
  }, [onClose, state.isDirty, state.isSaving, statusUpdating]);

  const confirmOverlay = (
    <AnimatePresence>
      {confirmState && (
        <ConfirmOverlay
          onCancel={() => setConfirmState(null)}
          onConfirm={() => confirmState.onConfirm()}
          title={confirmState.title}
          message={confirmState.message ?? ""}
          confirmLabel={confirmState.confirmLabel ?? "Confirm"}
          cancelLabel="Cancel"
          variant={confirmState.variant ?? "default"}
          disableActions={false}
        />
      )}
    </AnimatePresence>
  );

  if (state.lockInfo.lockBlocked) {
    const lockStatus = state.lockInfo.status;
    const lockedBy = state.lockInfo.lockedBy;
    const title =
      lockStatus === "waiting"
        ? "Another editor is active"
        : lockStatus === "lost"
          ? "You lost the editing lock"
          : "Checking lock…";
    const lockedByLabel =
      lockedBy?.userName || lockedBy?.userEmail || "Someone else";
    const lockedByDetail =
      lockedBy?.userEmail && lockedBy?.userName
        ? `${lockedBy.userName} (${lockedBy.userEmail})`
        : lockedBy?.userEmail || lockedBy?.userName || null;
    const description =
      lockStatus === "waiting"
        ? `${lockedByDetail ?? lockedByLabel} is editing this content.`
        : lockStatus === "lost"
          ? "Please try to re-acquire the lock."
          : "Ensuring exclusive access before editing.";
    return (
      <OverlayWrapper onClose={onClose} snapPoints={["97vh"]} draggable={false}>
        <div className="flex h-full flex-col items-center justify-center gap-4 px-6 py-8 text-center">
          {lockStatus === "loading" ? (
            <DotPulseLdr color="var(--color-slateCustom)" />
          ) : null}
          <div className="text-slateCustom space-y-2">
            <p className="text-xl font-semibold">{title}</p>
            <p className="text-sm text-gray-600">{description}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              className="rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700"
              onClick={onClose}
            >
              Close
            </button>
            {lockStatus !== "loading" ? (
              <button
                className="rounded-md bg-pinkCustom px-4 py-2 text-sm font-semibold text-white"
                onClick={() => state.lockInfo.retry()}
              >
                Retry
              </button>
            ) : null}
          </div>
        </div>
      </OverlayWrapper>
    );
  }

  if (state.isLoading && !data.content) {
    return (
      <>
        <OverlayWrapper onClose={requestClose} snapPoints={["97vh"]} draggable={false}>
          <div className="text-slateCustom flex h-full items-center justify-center px-6 py-8">
            Loading…
          </div>
        </OverlayWrapper>
        {confirmOverlay}
      </>
    );
  }

  const updateBlocks = (next: BlockBase[]) => {
    if (locale === "en") {
      handlers.setBlocksEn(next);
    } else {
      handlers.setBlocksFa(next);
    }
  };

  const flashSaveSuccess = () => {
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2000);
  };

  const handleSaveDraft = async () => {
    if (shouldGateNames) {
      setNameGateOpen(true);
      return;
    }
    const ok = await handlers.handleSave();
    if (ok) {
      flashSaveSuccess();
    }
  };

  const handleSavePublish = async () => {
    if (shouldGateNames) {
      setNameGateOpen(true);
      return;
    }
    const ok = await handlers.handleSaveAndPublish();
    if (ok) {
      flashSaveSuccess();
    }
  };

  const handleSaveNameGate = async () => {
    if (!session?.user?.id) {
      setNameError("You must be signed in to update your profile.");
      return;
    }
    const payload = {
      name_en: nameDraft.name_en.trim(),
      sur_name_en: nameDraft.sur_name_en.trim(),
      name_fa: nameDraft.name_fa.trim(),
      sur_name_fa: nameDraft.sur_name_fa.trim(),
    };
    if (
      !payload.name_en ||
      !payload.sur_name_en ||
      !payload.name_fa ||
      !payload.sur_name_fa
    ) {
      setNameError("Please fill in all name fields to continue.");
      return;
    }
    setNameSaving(true);
    setNameError(null);
    try {
      const res = await fetch(`/api/users/${session.user.id}/name`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.message || data?.error || "Failed to update name");
      }
      setLocalUserNames(payload);
      setNameGateOpen(false);
    } catch (err: any) {
      setNameError(err?.message || "Failed to update name fields.");
    } finally {
      setNameSaving(false);
    }
  };

  const handlePublish = async () => {
    await handlers.handlePublish();
  };

  const handleUnpublish = async () => {
    await handlers.handleUnpublish();
  };

  return (
    <>
      <OverlayWrapper
        onClose={requestClose}
        snapPoints={["97vh"]}
        draggable={false}
      >
        <div className="flex min-h-full flex-col">
          <div className="sticky top-0 z-40 flex items-center justify-between bg-white px-4 py-3">
            <button
              type="button"
              className="text-lg text-gray-500 hover:opacity-70 disabled:opacity-60"
              onClick={requestClose}
              disabled={state.isSaving || statusUpdating}
            >
              Close
            </button>
            {/* <AnimatePresence initial={false} mode="wait">
              {!titleInView && (
                <motion.span
                  key={`title-bar-${locale}`}
                  dir={locale === "fa" ? "rtl" : "ltr"}
                  className="truncate px-8 text-xl font-semibold"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 4 }}
                  transition={{ duration: 0.18, ease: "easeOut" }}
                >
                  {locale === "fa" ? data.titleFa : data.titleEn}
                </motion.span>
              )}
            </AnimatePresence> */}

            <div
              className="relative flex items-center gap-2 bg-pinkCustom rounded-sm w-36 justify-center"
              data-actions-menu-root="true"
            >
              <button
                type="button"
                className="text-white py-1 hover:opacity-70 disabled:opacity-60"
                onClick={handleSavePublish}
                disabled={state.isSaving || statusUpdating}
              >
                <motion.div
                  key={saveSuccess ? "saved" : "save-publish"}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.2 }}
                  className="flex items-center gap-1"
                >
                  {saveSuccess ? (
                    <>
                      <TikSVG className="w-4" svgColor="slateCustom" />
                      <span>Saved!</span>
                    </>
                  ) : state.isSaving || statusUpdating ? (
                    <div className="flex items-center justify-center w-20">
                      <DotPulseLdr
                        size={30}
                        speed={2}
                        color="var(--color-white)"
                      />
                    </div>
                  ) : (
                    "Save Publish"
                  )}
                </motion.div>
              </button>
              <button
                type="button"
                className="border-l border-gray-200 pl-2 text-white disabled:opacity-60"
                onClick={() => setActionsMenuOpen((prev) => !prev)}
                disabled={state.isSaving || statusUpdating}
                aria-label="More actions"
              >
                <ChevronDown className="h-5 w-5" />
              </button>

              <AnimatePresence>
                {actionsMenuOpen ? (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 top-full z-50 mt-2 w-36 py-2 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg"
                  >
                    <button
                      type="button"
                      className="w-full px-4 py-1 text-left hover:bg-slate-50 disabled:opacity-60 text-slateCustom"
                      onClick={() => {
                        setActionsMenuOpen(false);
                        void handleSaveDraft();
                      }}
                      disabled={isArchived || state.isSaving || statusUpdating}
                    >
                      Save draft
                    </button>
                    <hr className="bg-gray-200 text-gray-200 h-px ml-auto w-[calc(100%-16px)]" />
                    <button
                      type="button"
                      className="w-full px-4 py-1 text-left hover:bg-slate-50 disabled:opacity-60 text-amber-600"
                      onClick={() => {
                        setActionsMenuOpen(false);
                        setDiscardDraftConfirmOpen(true);
                      }}
                      disabled={!hasContentId || isArchived || statusUpdating}
                    >
                      Discard draft
                    </button>
                    <hr className="bg-gray-200 text-gray-200 h-px ml-auto w-[calc(100%-16px)]" />
                    <button
                      type="button"
                      className={`w-full px-4 py-1 text-left hover:bg-slate-50 disabled:opacity-60 ${isPublished? "text-amber-600":"text-green-600"}`}
                      onClick={() => {
                        setActionsMenuOpen(false);
                        if (isPublished) {
                          void handleUnpublish();
                        } else {
                          void handlePublish();
                        }
                      }}
                      disabled={!hasContentId || isArchived || statusUpdating}
                    >
                      {isPublished ? "Unpublish" : "Publish"}
                    </button>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          </div>
          <div className="flex-1 px-4 pb-6">
            <div className="mx-auto flex w-full max-w-6xl flex-col space-y-3">
              <header className="mt-4 flex flex-wrap items-center gap-2">
                <div
                  className="relative min-w-[240px] flex-1"
                  dir={locale === "fa" ? "rtl" : "ltr"}
                >
                  <input
                    ref={titleRef}
                    type="text"
                    required
                    className={[
                      "peer block w-full bg-transparent px-2 py-2 wrap-normal",
                      "border-0 border-b ring-0 outline-none",
                      "border-slateCustom/70 focus:border-slateCustom",
                      "placeholder-transparent",
                      locale === "fa" ? "text-right" : "text-left",
                      isArchived
                        ? "cursor-not-allowed bg-gray-100 text-gray-500"
                        : "",
                    ].join(" ")}
                    placeholder={`title_${locale}`}
                    value={locale === "fa" ? data.titleFa : data.titleEn}
                    onChange={(event) => {
                      const nextTitle = event.target.value;
                      if (locale === "fa") {
                        handlers.setTitleFa(nextTitle);
                      } else {
                        handlers.setTitleEn(nextTitle);
                      }
                    }}
                    disabled={isArchived}
                    readOnly={isArchived}
                    aria-disabled={isArchived}
                    dir={locale === "fa" ? "rtl" : "ltr"}
                  />

                  <span className="pointer-events-none absolute right-0 -bottom-px left-0 h-[2px] bg-slate-300/50" />
                  <span className="bg-slateCustom pointer-events-none absolute -bottom-px left-1/2 h-[2px] w-0 -translate-x-1/2 transition-[width] duration-200 ease-out peer-focus:w-full" />

                  <label
                    className={[
                      "absolute top-2 left-2 text-slate-500 transition-all duration-200",
                      "peer-placeholder-shown:top-2 peer-placeholder-shown:text-base",
                      "peer-focus:text-slateCustom peer-focus:-top-3 peer-focus:text-sm",
                      "peer-valid:-top-3 peer-valid:text-sm",
                      isArchived ? "text-gray-400" : "",
                    ].join(" ")}
                  >
                    {`title_${locale}`}
                  </label>
                </div>
              </header>

              <div className="grid grid-cols-1 items-start gap-4">
                <DetailsPanelMobile
                  open={detailsOpen}
                  onToggleOpen={() => setDetailsOpen((prev) => !prev)}
                  isArchived={isArchived}
                  durationValue={durationValue}
                  durationUnit={durationUnit}
                  onDurationInputChange={handleDurationInputChange}
                  onDurationUnitChange={handleDurationUnitChange}
                  formattedDisplayDate={formattedDisplayDate}
                  datePickerRef={datePickerRef}
                  datePickerValue={datePickerValue}
                  onDatePickerChange={handleDatePickerChange}
                  openDatePicker={openDatePicker}
                  selectedTags={selectedTags}
                  tagsDisabled={isArchived}
                  onOpenTagManager={() => setTagManagerOpen(true)}
                  authorFaQuery={authorQueries.fa}
                  authorEnQuery={authorQueries.en}
                  authorFa={authorFa}
                  authorEn={authorEn}
                  authorSearchResults={authorSearchResults}
                  authorSearchLoading={authorSearchLoading}
                  onAuthorQueryChange={handleAuthorQueryChange}
                  onAuthorSelect={handleAuthorSelect}
                  onAuthorClear={handleAuthorClear}
                />

                <div className="flex items-center justify-between gap-3">
                  {(() => {
                    const list = locale === "en" ? blocks_en : blocks_fa;
                    const collapsed = collapsedByLocale[locale];
                    const allCollapsed =
                      list.length > 0 && list.every((b) => collapsed.has(b.id));
                    return (
                      <button
                        type="button"
                        className="flex-1 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                        onClick={toggleCollapseAll}
                        disabled={list.length === 0}
                      >
                        {allCollapsed ? "Expand all" : "Collapse all"}
                      </button>
                    );
                  })()}
                  <button
                    type="button"
                    className="flex-1 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                    onClick={() => handlers.handlePreview(locale)}
                    disabled={isArchived || state.isSaving}
                  >
                    Preview
                  </button>
                </div>

                <BlockEditor
                  value={locale === "en" ? blocks_en : blocks_fa}
                  onChange={updateBlocks}
                  locale={locale}
                  setLocale={setLocale}
                  isArchived={isArchived}
                  allowedBlockTypes={allowedBlockTypes}
                  onCloneContentBlocks={(payload) =>
                    handlers.handleCloneContentBlocks(payload)
                  }
                  contentId={data.contentId ?? undefined}
                  collapsedBlockIds={collapsedByLocale[locale]}
                  onToggleCollapseBlock={toggleCollapseBlock}
                />
                {data.contentId ? (
                  <div>
                    <AuditPanel contentId={data.contentId} />
                  </div>
                ) : null}
              </div>
              <div
                className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-slate-700"
                dir="ltr"
              >
                <span className="font-semibold text-slate-800">Trace #:</span>
                <span className="font-mono">
                  {traceNumber ?? (data.contentId ? "—" : "Will be generated on save")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </OverlayWrapper>

      {tagManagerOpen ? (
        <OverlayWrapper
          onClose={() => setTagManagerOpen(false)}
          snapPoints={[ "95vh"]}
          draggable
          aria-label="Manage tags"
        >
          <div className="flex min-h-full flex-col gap-4 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-base font-semibold text-slateCustom">
                  Manage tags
                </p>
                <p className="text-sm text-slate-500">
                  Add or remove tags for this content.
                </p>
              </div>
              <button
                type="button"
                className="rounded-md border border-gray-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-gray-100"
                onClick={() => setTagManagerOpen(false)}
              >
                Close
              </button>
            </div>
            <div className="flex-1 overflow-auto space-y-3">
              <TagManagerList
                selectedTags={selectedTags}
                tagManagerDisabled={isArchived}
                tagFilter={tagFilter}
                onTagFilterChange={setTagFilter}
                onClearFilter={() => setTagFilter("")}
                onRemoveTag={(tagId) => handlers.removeTag(tagId)}
                tagsLoading={tagsLoading}
                tagsError={tagsError}
                allTags={allTags}
                onAddTag={(tag) => handlers.addTag(tag)}
              />
            </div>
          </div>
        </OverlayWrapper>
      ) : null}

      <DiscardDraftModal
        open={discardDraftConfirmOpen}
        onOpenChange={setDiscardDraftConfirmOpen}
        disabled={statusUpdating}
        onConfirm={async () => {
          await handlers.handleDiscardDraft();
          setDiscardDraftConfirmOpen(false);
        }}
      />

      <CustomModal
        open={Boolean(state.error)}
        onOpenChange={(open) => {
          if (!open) handlers.clearError();
        }}
        maxWidth="max-w-md"
      >
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-slate-800">
            Action failed
          </h3>
          <pre className="whitespace-pre-wrap text-sm text-slate-600">
            {state.error}
          </pre>
          {state.errorIssues && state.errorIssues.length > 0 ? (
            <div className="space-y-2">
              {state.errorIssues.map((issue, index) => (
                <div
                  key={`${issue.blockId ?? "issue"}-${index}`}
                  className="flex items-start justify-between gap-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-slate-700"
                >
                  <div className="min-w-0">
                    <div className="font-medium">
                      {issue.blockId
                        ? `Block ${issue.blockId}`
                        : issue.field
                          ? issue.field
                          : "Issue"}
                    </div>
                    <div className="text-slate-600">{issue.message}</div>
                  </div>
                  {issue.blockId ? (
                    <button
                      type="button"
                      className="shrink-0 rounded-md border border-pinkCustom px-2 py-1 text-xs font-semibold text-pinkCustom hover:bg-pinkCustom/10"
                      onClick={() => handleGoToBlock(issue.blockId)}
                    >
                      Go to block
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}
          <div className="flex justify-end">
            <button
              type="button"
              className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-slate-800"
              onClick={() => handlers.clearError()}
            >
              Close
            </button>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        open={authorProfileModalOpen}
        onOpenChange={(open) => {
          setAuthorProfileModalOpen(open);
          if (!open) {
            setAuthorProfileDraft(null);
            setAuthorProfileError(null);
          }
        }}
        maxWidth="max-w-lg"
      >
        <div className="space-y-4">
          <div>
            <p className="text-base font-semibold text-slate-800">
              Complete author profile
            </p>
            <p className="text-sm text-slate-500">
              This operator needs a complete localized profile before staying assigned as an author.
            </p>
          </div>
          {authorProfileDraft ? (
            <div className="space-y-3">
              <div className="rounded-md border border-gray-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                {authorProfileDraft.email}
              </div>
              <input
                type="text"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-pinkCustom disabled:bg-slate-100"
                placeholder="Name (English)"
                value={authorProfileDraft.name_en}
                onChange={(event) =>
                  setAuthorProfileDraft((prev) =>
                    prev ? { ...prev, name_en: event.target.value } : prev,
                  )
                }
                disabled={authorProfileSaving}
              />
              <input
                type="text"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-pinkCustom disabled:bg-slate-100"
                placeholder="Surname (English)"
                value={authorProfileDraft.sur_name_en}
                onChange={(event) =>
                  setAuthorProfileDraft((prev) =>
                    prev
                      ? { ...prev, sur_name_en: event.target.value }
                      : prev,
                  )
                }
                disabled={authorProfileSaving}
              />
              <input
                type="text"
                dir="rtl"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-pinkCustom disabled:bg-slate-100"
                placeholder="نام (فارسی)"
                value={authorProfileDraft.name_fa}
                onChange={(event) =>
                  setAuthorProfileDraft((prev) =>
                    prev ? { ...prev, name_fa: event.target.value } : prev,
                  )
                }
                disabled={authorProfileSaving}
              />
              <input
                type="text"
                dir="rtl"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-pinkCustom disabled:bg-slate-100"
                placeholder="نام خانوادگی (فارسی)"
                value={authorProfileDraft.sur_name_fa}
                onChange={(event) =>
                  setAuthorProfileDraft((prev) =>
                    prev
                      ? { ...prev, sur_name_fa: event.target.value }
                      : prev,
                  )
                }
                disabled={authorProfileSaving}
              />
            </div>
          ) : null}
          {authorProfileError ? (
            <p className="text-xs text-red-500">{authorProfileError}</p>
          ) : null}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-slate-600"
              onClick={() => setAuthorProfileModalOpen(false)}
              disabled={authorProfileSaving}
            >
              Close
            </button>
            <button
              type="button"
              className="rounded-md bg-pinkCustom px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60"
              onClick={handleSaveAuthorProfile}
              disabled={authorProfileSaving || !authorProfileDraft}
            >
              {authorProfileSaving ? "Saving..." : "Save profile"}
            </button>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        open={nameGateOpen}
        onOpenChange={(open) => setNameGateOpen(open)}
        maxWidth="max-w-md"
      >
        <div className="space-y-4">
          <div>
            <p className="text-base font-semibold text-slate-800">
              Complete your profile
            </p>
            <p className="text-sm text-slate-500">
              Please add your name and surname in both English and Farsi before
              creating new content.
            </p>
          </div>
          <div className="space-y-3">
            <input
              type="text"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-pinkCustom disabled:bg-slate-100"
              placeholder="Name (English)"
              value={nameDraft.name_en}
              onChange={(e) =>
                setNameDraft((prev) => ({ ...prev, name_en: e.target.value }))
              }
              disabled={nameSaving || !missingNameFields.name_en}
            />
            <input
              type="text"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-pinkCustom disabled:bg-slate-100"
              placeholder="Surname (English)"
              value={nameDraft.sur_name_en}
              onChange={(e) =>
                setNameDraft((prev) => ({ ...prev, sur_name_en: e.target.value }))
              }
              disabled={nameSaving || !missingNameFields.sur_name_en}
            />
            <input
              type="text"
              dir="rtl"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-pinkCustom disabled:bg-slate-100"
              placeholder="نام (فارسی)"
              value={nameDraft.name_fa}
              onChange={(e) =>
                setNameDraft((prev) => ({ ...prev, name_fa: e.target.value }))
              }
              disabled={nameSaving || !missingNameFields.name_fa}
            />
            <input
              type="text"
              dir="rtl"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-pinkCustom disabled:bg-slate-100"
              placeholder="نام خانوادگی (فارسی)"
              value={nameDraft.sur_name_fa}
              onChange={(e) =>
                setNameDraft((prev) => ({ ...prev, sur_name_fa: e.target.value }))
              }
              disabled={nameSaving || !missingNameFields.sur_name_fa}
            />
          </div>
          {nameError ? (
            <p className="text-xs text-red-500">{nameError}</p>
          ) : null}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm text-slate-600"
              onClick={() => setNameGateOpen(false)}
              disabled={nameSaving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-md bg-pinkCustom px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60"
              onClick={handleSaveNameGate}
              disabled={nameSaving}
            >
              {nameSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </CustomModal>
      {confirmOverlay}
    </>
  );
}
